import React from 'react';
import { Link } from 'react-router-dom';
import './CategoryHero.css';

/**
 * Phase 4 landing-page hero for Era / Series / Set / Game pages. Renders
 * the leaf category's branding (logo, release date, total cards, hero
 * image) plus three sub-CTAs adapted to the category kind:
 *
 *   game / era / series  →  [Sealed] [Singles] [Accessories]
 *   set                  →  [Sealed in this set] [Singles in this set] [Sets like this]
 *
 * Falls back to a clean tinted block when no imagery is configured yet.
 */
export default function CategoryHero({ leaf, basePath }) {
  if (!leaf) return null;
  const { name, kind, logo_url, hero_image_url, set_code, release_date, total_cards, description } = leaf;

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return null;
      return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return null; }
  };

  const ctas = kind === 'set' ? [
    { label: 'Sealed product',  href: `${basePath}?tag=booster-box,etb,collection-box`, accent: 'sealed' },
    { label: 'Singles',         href: `${basePath}?tag=singles`,                         accent: 'singles' },
    { label: 'Graded slabs',    href: `${basePath}?tag=graded-slabs`,                    accent: 'graded' },
  ] : [
    { label: 'Sealed product',  href: `${basePath}?tag=booster-box,etb,collection-box`, accent: 'sealed' },
    { label: 'Singles',         href: `${basePath}?tag=singles`,                         accent: 'singles' },
    { label: 'Accessories',     href: `${basePath}?tag=sleeves,deck-boxes,binders`,      accent: 'accessories' },
  ];

  return (
    <section className={`cat-hero cat-hero--${kind || 'set'}`}
             style={hero_image_url ? { '--cat-hero-bg': `url("${hero_image_url}")` } : undefined}>
      <div className="cat-hero__overlay" />
      <div className="cat-hero__body">
        <div className="cat-hero__brand">
          {logo_url && (
            <img className="cat-hero__logo" src={logo_url} alt={`${name} logo`} loading="eager" />
          )}
          <div className="cat-hero__title">
            <span className="cat-hero__kind">{(kind || '').replace('_', ' ').toUpperCase()}</span>
            <h1>{name}</h1>
            <div className="cat-hero__meta">
              {set_code && <span><strong>{set_code}</strong></span>}
              {fmtDate(release_date) && <span>Released {fmtDate(release_date)}</span>}
              {total_cards && <span>{total_cards} cards</span>}
            </div>
            {description && <p className="cat-hero__desc">{description}</p>}
          </div>
        </div>

        <div className="cat-hero__ctas">
          {ctas.map(c => (
            <Link key={c.label} to={c.href} className={`cat-hero__cta cat-hero__cta--${c.accent}`}>
              <span>{c.label}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
